import {useRef} from "react";
import {Tooltip} from "components/tooltip/Tooltip";
import {SHARED_TOOLTIP_ID, TRUNCATION_TOLERANCE_PX} from "components/tooltip/AppTooltip.component";
import type {DocumentDataFieldProps, DocumentDataGridProps} from "types/documentData";
import {WarningIcon, WarningsTooltip} from "./DocumentWarnings.component";

/**
 * One field of the document data: the label over the value, as in the voucher details module of
 * the new task details design. A value too long for the column is cut off and repeated in a
 * tooltip; a field with warnings is flagged and lists them in a tooltip of its own.
 * Rendered inside a DocumentDataGrid.
 */
export const DocumentDataField = ({id, label, value, children, warnings, wide, dataCy}: DocumentDataFieldProps) => {
    const valueRef = useRef<HTMLElement>(null);
    const hasWarnings = !!warnings?.length;
    const warningsTooltipId = `document-data-${id}-warnings-tooltip`;
    const valueTooltipId = `document-data-${id}-value-tooltip`;

    // measured on hover, so a value that fits returns null and react-tooltip skips the empty tip
    const getValueTooltipContent = () => {
        const element = valueRef.current;
        if (!element || element.scrollWidth - element.clientWidth <= TRUNCATION_TOLERANCE_PX) {
            return null;
        }
        return value;
    };

    const className = "document-data__field"
        + (wide ? " document-data__field--wide" : "")
        + (hasWarnings ? " document-data__field--warning" : "")
        + (children ? " document-data__field--content" : "");

    return (
        <div className={className}
             data-cy={dataCy}
             data-tip={hasWarnings ? warningsTooltipId : undefined}
             data-for={hasWarnings ? SHARED_TOOLTIP_ID : undefined}>
            <dt className="document-data__label">
                <span className="document-data__label-text">{label}</span>
                {hasWarnings &&
                    // the keyboard's way to the warnings: focusing it shows the field's tooltip
                    <button type="button" className="document-data__warning-trigger">
                        <WarningIcon/>
                        <span className="sr-only">
                            {warnings.map(warning => `${warning.source}: ${warning.message}`).join(". ")}
                        </span>
                    </button>}
            </dt>
            {children ?
                <dd className="document-data__value">{children}</dd> :
                <dd className="document-data__value document-data__value--truncated"
                    ref={valueRef}
                    data-tip={valueTooltipId}
                    data-for={SHARED_TOOLTIP_ID}>
                    {value}
                </dd>}
            {!children && <Tooltip id={valueTooltipId} content={getValueTooltipContent}/>}
            {hasWarnings && <WarningsTooltip id={warningsTooltipId} warnings={warnings}/>}
        </div>
    );
};

/** The grid the fields of a document are laid out in: three columns, two in a narrow panel. */
export const DocumentDataGrid = ({children}: DocumentDataGridProps) => (
    <dl className="document-data__grid">{children}</dl>
);
